<script lang="ts">
  // ============================================================
  // Svelte 4 Legacy Mode Component
  // ------------------------------------------------------------
  // @inertiajs/svelte 호환성을 위해 Svelte 4 문법 유지
  // ============================================================

  import { router } from '@inertiajs/svelte'
  import { onMount } from 'svelte'
  import Button from '@/components/ui/Button.svelte'

  export const sso_providers: string[] = []
  export let flash: Record<string, string> = {}

  const ONE_PASS_AUTHORIZE_URL = '/auth/1pass/initiate'

  let email = ''
  let password = ''
  let isSubmitting = false
  // 계정 삭제 직후 안내. 삭제는 API(`DELETE /api/v1/account`)로 끝나 서버 flash 를
  // 실을 자리가 없어, 이동 시 붙인 쿼리 하나로만 판단한다.
  let deletedNotice = false
  let showInAppFallback = false
  let copied = false

  onMount(() => {
    deletedNotice = new URLSearchParams(window.location.search).get('deleted') === '1'
  })

  function handleSubmit(e: Event) {
    e.preventDefault()
    isSubmitting = true

    router.post('/login', {
      email,
      password
    }, {
      onFinish: () => {
        isSubmitting = false
      }
    })
  }

  // @ts-ignore - used in template onclick handlers
  function handleSSO(provider: string) {
    window.location.href = `/auth/${provider}/callback`
  }

  // logi(1pass) 버튼 환경별 분기 — docs/integrations/buttons.md + krx_listing 참조 패턴.
  //   1) 인앱 WebView (KakaoTalk/Line/Instagram/NAVER 등) → Universal Link 트리거
  //      불가 → inline 안내 카드로 외부 브라우저 유도
  //   2) iOS + non-Safari (iOS Chrome/Firefox/Edge) → x-safari-https:// 비공식
  //      스킴으로 Safari 핸드오프 (Universal Links는 Safari/WKWebView 에서만 트리거됨)
  //   3) Android / iOS Safari / 데스크톱(macOS 포함) → 기본 navigation
  //
  // 🔴 macOS 는 핸드오프 대상이 아니다 (2026-09-17 실측). Mac 크롬까지 Safari 로 넘기면
  //    로그인이 크롬이 아니라 Safari 에서 끝나 — 크롬은 /login 에 그대로 남고 Safari 에
  //    세션이 생긴다. Mac 비-Safari 는 logi 로그인 페이지가 `logi:///` 커스텀 스킴
  //    ("Mac 앱에서 열기")·푸시·QR 로 같은 탭 안에서 처리한다. logi 서버 자신도
  //    핸드오프를 iOS 로 한정한다(sessions_controller `non_safari_browser? && ios_browser?`).
  function detectClientEnv() {
    const ua = navigator.userAgent
    const isIOS = /iPhone|iPad|iPod/.test(ua)
    const isNonSafariBrowser = /CriOS|FxiOS|EdgiOS|Chrome|Firefox|Edg|OPR\//.test(ua)
    const isInAppWebView = /KAKAOTALK|Line\/|Instagram|FBAN|FBAV|NAVER\(inapp|DaumApps|; wv\)/i.test(ua)
    return { isIOS, isNonSafariBrowser, isInAppWebView }
  }

  function handleOnePassClick(e: MouseEvent) {
    if (typeof navigator === 'undefined') return
    const env = detectClientEnv()

    if (env.isInAppWebView) {
      e.preventDefault()
      showInAppFallback = true
      return
    }

    if (env.isIOS && env.isNonSafariBrowser) {
      e.preventDefault()
      const absoluteUrl = new URL(ONE_PASS_AUTHORIZE_URL, window.location.origin).toString()
      window.location.href = absoluteUrl.replace(/^https:/, 'x-safari-https:')
      return
    }
    // 그 외: 기본 anchor navigation 진행 (OS 가 logi 앱으로 라우팅)
  }

  async function copyAuthorizeUrl() {
    try {
      const absoluteUrl = new URL(ONE_PASS_AUTHORIZE_URL, window.location.origin).toString()
      await navigator.clipboard.writeText(absoluteUrl)
      copied = true
      setTimeout(() => (copied = false), 2000)
    } catch (_err) {
      // private mode / quota — 무시
    }
  }
</script>

<div class="min-h-screen bg-gray-50 dark:bg-dark-primary flex items-center justify-center px-4 py-12">
  <div class="max-w-[420px] w-full">
    <!-- Logo & Title -->
    <div class="text-center mb-10">
      <a href="/" class="inline-flex items-center justify-center gap-2.5 mb-3">
        <div class="w-10 h-10 bg-gradient-to-br from-primary to-primary-700 rounded-2xl flex items-center justify-center shadow-md">
          <span class="text-white font-bold text-sm">AX</span>
        </div>
      </a>
      <h1 class="text-h1 text-content">AX Hub</h1>
      <p class="text-body2 text-content-tertiary mt-2">AI 전문가 커뮤니티에 로그인하세요</p>
    </div>

    <!-- Login Card -->
    <div class="card p-7 sm:p-8 shadow-lg">
      <!-- 계정 삭제 완료 안내 -->
      {#if deletedNotice}
        <div class="mb-6 p-4 bg-success-light dark:bg-success/10 border border-success/20 rounded-xl" role="status">
          <p class="text-body2 text-success-dark dark:text-success">
            계정을 삭제했습니다.<br />
            6개월 안에 같은 계정으로 다시 로그인하면 되살릴 수 있습니다.
          </p>
        </div>
      {/if}

      <!-- Flash Messages -->
      {#if flash.error}
        <div class="mb-6 p-4 bg-error-light dark:bg-error/10 border border-error/20 rounded-xl flex items-start gap-3" role="alert">
          <svg class="w-5 h-5 text-error shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <p class="text-body2 text-error">{flash.error}</p>
        </div>
      {/if}

      {#if flash.success}
        <div class="mb-6 p-4 bg-success-light dark:bg-success/10 border border-success/20 rounded-xl flex items-start gap-3" role="status">
          <svg class="w-5 h-5 text-success shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <p class="text-body2 text-success-dark dark:text-success">{flash.success}</p>
        </div>
      {/if}

      <!-- logi (1pass) SSO — 4x1 hero CTA (docs/integrations/buttons.md)
           Apple Universal Link 는 Safari/WKWebView 에서만 트리거되므로
           non-Safari 브라우저는 Safari 로 핸드오프, 인앱 WebView 는 fallback 카드. -->
      <a
        href={ONE_PASS_AUTHORIZE_URL}
        onclick={handleOnePassClick}
        data-turbo="false"
        rel="external"
        aria-label="logi 로 로그인"
        class="w-full inline-flex items-center justify-center gap-2 h-14 px-6 rounded-xl bg-gray-900 text-white font-semibold text-base hover:opacity-90 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-700 dark:bg-white dark:text-gray-900 dark:focus-visible:ring-gray-300 transition"
      >
        <img src="/logi-icon.svg" alt="" aria-hidden="true" class="w-6 h-6 shrink-0" />
        logi 로 로그인
      </a>

      {#if showInAppFallback}
        <!-- 인앱 브라우저(카톡/라인/네이버 등) fallback: deep-link 동작 안 하므로 URL 직접 노출 -->
        <div class="mt-4 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <div class="flex items-start gap-3">
            <svg class="w-5 h-5 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.48 0L3.16 16.25A2 2 0 005 19z" />
            </svg>
            <div class="flex-1 min-w-0">
              <p class="font-semibold text-amber-900 dark:text-amber-100">인앱 브라우저 감지</p>
              <p class="text-body2 text-amber-800 dark:text-amber-200 mt-1">
                카카오톡/라인/네이버 등 인앱 브라우저에서는 logi 앱이 자동으로 열리지 않습니다. 외부 브라우저(Safari/Chrome)에서 열어주세요.
              </p>
              <code class="mt-3 block w-full p-2 text-xs font-mono break-all rounded bg-white dark:bg-dark-secondary border border-amber-200 dark:border-amber-800 text-gray-800 dark:text-dark-text-medium select-all">{ONE_PASS_AUTHORIZE_URL}</code>
              <div class="flex gap-2 mt-3 flex-wrap">
                <a
                  href={ONE_PASS_AUTHORIZE_URL}
                  target="_blank"
                  rel="external noopener"
                  class="px-3 py-1.5 text-body2 font-semibold rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition-colors"
                >
                  새 탭에서 열기
                </a>
                <button
                  type="button"
                  onclick={copyAuthorizeUrl}
                  class="px-3 py-1.5 text-body2 font-semibold rounded-lg border border-amber-600 dark:border-amber-400 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
                >
                  {copied ? '복사됨' : 'URL 복사'}
                </button>
                <button
                  type="button"
                  onclick={() => (showInAppFallback = false)}
                  class="px-3 py-1.5 text-body2 font-medium rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-tertiary transition-colors"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      {/if}

      <!-- Divider: 또는 -->
      <div class="relative my-7">
        <div class="absolute inset-0 flex items-center">
          <div class="w-full border-t border-default/60"></div>
        </div>
        <div class="relative flex justify-center">
          <span class="px-4 bg-surface text-caption text-content-tertiary">또는 이메일로</span>
        </div>
      </div>

      <!-- Login Form -->
      <form onsubmit={handleSubmit} class="space-y-5">
        <div>
          <label for="email" class="block text-label text-gray-700 dark:text-dark-text-medium mb-2">이메일</label>
          <input
            type="email"
            id="email"
            bind:value={email}
            required
            autocomplete="email"
            class="input"
            placeholder="you@company.com"
          />
        </div>

        <div>
          <label for="password" class="block text-label text-gray-700 dark:text-dark-text-medium mb-2">비밀번호</label>
          <input
            type="password"
            id="password"
            bind:value={password}
            required
            autocomplete="current-password"
            class="input"
            placeholder="8자 이상 입력"
          />
        </div>

        <div class="flex items-center justify-between">
          <label class="flex items-center gap-2 text-body2 text-content-tertiary cursor-pointer select-none">
            <input
              type="checkbox"
              class="w-4 h-4 rounded-md bg-surface-tertiary border-gray-300 dark:border-dark-border text-primary focus:ring-primary/30 focus:ring-offset-0 cursor-pointer"
            />
            <span>로그인 유지</span>
          </label>
          <a href="/password_resets/new" class="text-body2 text-primary hover:text-primary-dark font-medium transition-colors">
            비밀번호 찾기
          </a>
        </div>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={isSubmitting}
          loadingLabel="로그인 중..."
        >
          로그인
        </Button>
      </form>

      <!-- SSO Divider -->
      <!-- {#if sso_providers.length > 0}
        <div class="relative my-7">
          <div class="absolute inset-0 flex items-center">
            <div class="w-full border-t border-default/60"></div>
          </div>
          <div class="relative flex justify-center">
            <span class="px-4 bg-surface text-caption text-content-tertiary">또는</span>
          </div>
        </div>

        SSO Buttons
        <div class="space-y-3">
          {#if sso_providers.includes('google')}
            <button
              onclick={() => handleSSO('google')}
              class="w-full py-3 px-4 bg-surface text-gray-800 dark:text-dark-text-high font-medium rounded-xl border border-default hover:bg-gray-50 dark:hover:bg-dark-elevated focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 transition-all cursor-pointer flex items-center justify-center gap-3"
            >
              <svg class="w-5 h-5 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Google로 계속하기
            </button>
          {/if}

          {#if sso_providers.includes('apple')}
            <button
              onclick={() => handleSSO('apple')}
              class="w-full py-3 px-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-medium rounded-xl hover:bg-gray-800 dark:hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-600 transition-all cursor-pointer flex items-center justify-center gap-3"
            >
              <svg class="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              Apple로 계속하기
            </button>
          {/if}
        </div>
      {/if} -->

      <!-- Register Link -->
      <p class="mt-7 text-center text-body2 text-content-tertiary">
        아직 계정이 없으신가요?
        <a href="/register" class="text-primary hover:text-primary-dark font-semibold transition-colors ml-1">
          회원가입
        </a>
      </p>
    </div>

    <!-- Footer -->
    <p class="mt-8 text-center text-caption text-gray-300 dark:text-dark-text-disabled leading-relaxed">
      로그인함으로써
      <a href="/terms" class="hover:text-gray-500 dark:hover:text-dark-text-low underline underline-offset-2 transition-colors">이용약관</a>
      및
      <a href="/privacy" class="hover:text-gray-500 dark:hover:text-dark-text-low underline underline-offset-2 transition-colors">개인정보처리방침</a>에 동의합니다.
    </p>
  </div>
</div>
